// src/components/KitchenPanel.jsx  —  ohapotato v2026.10.36
//
// Toolbar widget with two tabs:
//   🥔 My Pantry  — mark what you have at home (all users)
//   🛒 Grocery List — items needed to buy (all users)
//
// Rendered inline (dropdown on desktop, bottom sheet on mobile).
// Portal-free — lives in the toolbar, not above the page.

import React, { useState, useRef, useEffect } from 'react';
import { INGREDIENTS, CATEGORIES } from '../data/ingredients';

function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth <= 640);
  useEffect(() => {
    const fn = () => setM(window.innerWidth <= 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return m;
}

// ── Categorise a string for the grocery list ─────────────────────────────────
const CAT_ORDER = ['Produce','Proteins','Dairy','Grains & Legumes','Pantry & Spices'];
const CAT_EMOJI_MAP = { Proteins:'🥩', Dairy:'🧀', Produce:'🥦', 'Grains & Legumes':'🌾', 'Pantry & Spices':'🧂' };

function categorise(name) {
  const l = (name || '').toLowerCase();
  if (/\b(chicken|beef|pork|salmon|tuna|shrimp|prawn|egg|tofu|lamb|turkey|fish|bacon|sausage)\b/.test(l)) return 'Proteins';
  if (/\b(milk|cheese|yogurt|cream|butter|ghee|cheddar|mozzarella)\b/.test(l))                           return 'Dairy';
  if (/\b(lettuce|spinach|tomato|onion|garlic|pepper|carrot|broccoli|zucchini|mushroom|potato|kale|cucumber|apple|lemon|lime|banana|berry|avocado|mango|herb|cilantro|parsley|basil|ginger)\b/.test(l)) return 'Produce';
  if (/\b(flour|rice|pasta|bread|oat|quinoa|lentil|bean|chickpea|noodle|wrap|tortilla|bagel|pita)\b/.test(l)) return 'Grains & Legumes';
  return 'Pantry & Spices';
}

// ── My Pantry tab ─────────────────────────────────────────────────────────────
function PantryTab({ pantry, onToggle, onClear }) {
  const [search, setSearch] = useState('');
  const [cat,    setCat   ] = useState('all');

  const visible = INGREDIENTS.filter(i => {
    const matchCat    = cat === 'all' || i.cat === cat;
    const matchSearch = i.label.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const ownedCount = pantry.size;

  return (
    <div style={pt.wrap}>
      {/* Search */}
      <div style={pt.searchRow}>
        <input style={pt.searchInput} placeholder="Search ingredients…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {ownedCount > 0 && (
          <button style={pt.clearBtn} onClick={onClear}>Clear all ({ownedCount})</button>
        )}
      </div>

      {/* Category filter */}
      <div style={pt.catRow}>
        {CATEGORIES.map(c => (
          <button key={c.id}
            style={{ ...pt.catBtn, ...(cat === c.id ? pt.catBtnActive : {}) }}
            onClick={() => setCat(c.id)}
            onMouseDown={e => e.preventDefault()}>{c.label}
          </button>
        ))}
      </div>

      {/* Instruction */}
      <p style={pt.hint}>Tap ingredients you already have at home ✓</p>

      {/* Grid */}
      <div style={pt.grid}>
        {visible.map(ing => {
          const owned = pantry.has(ing.id);
          return (
            <button key={ing.id}
              style={{ ...pt.tile, ...(owned ? pt.tileOwned : {}) }}
              onClick={() => onToggle(ing.id)}
              onMouseDown={e => e.preventDefault()}
            >
              <span style={pt.tileEmoji}>{ing.emoji}</span>
              <span style={pt.tileLabel}>{ing.label}</span>
              {owned && <span style={pt.tileCheck}>✓</span>}
            </button>
          );
        })}
      </div>

      {ownedCount > 0 && (
        <div style={pt.footer}>
          <span style={pt.footerText}>🥔 {ownedCount} item{ownedCount !== 1 ? 's' : ''} in your pantry</span>
        </div>
      )}
    </div>
  );
}

// ── Grocery List tab ──────────────────────────────────────────────────────────
function GroceryTab({ items, onToggle, onRemove, onClearDone, onClearAll, onAdd }) {
  const [input, setInput] = useState('');

  const handleAdd = () => {
    if (!input.trim()) return;
    onAdd(input.trim());
    setInput('');
  };

  const doneCount  = items.filter(i => i.checked).length;
  const totalCount = items.length;

  // Group by category
  const grouped = CAT_ORDER.reduce((acc, cat) => {
    const catItems = items.filter(i => categorise(i.name) === cat);
    if (catItems.length) acc[cat] = catItems;
    return acc;
  }, {});

  return (
    <div style={gt.wrap}>
      {/* Add row */}
      <div style={gt.addRow}>
        <input style={gt.addInput}
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
          placeholder="Add an item… press Enter"
        />
        <button style={gt.addBtn} onClick={handleAdd}>+ Add</button>
      </div>

      {/* Progress */}
      {totalCount > 0 && (
        <div style={gt.progressTrack}>
          <div style={{ ...gt.progressFill, width: `${(doneCount / totalCount) * 100}%` }} />
        </div>
      )}

      {/* List */}
      <div style={gt.list}>
        {totalCount === 0 ? (
          <div style={gt.empty}>
            <span style={gt.emptyIcon}>🛒</span>
            <p style={gt.emptyText}>Your list is empty</p>
            <p style={gt.emptySub}>Items added from recipe cards or the Meal Planner appear here.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([cat, catItems]) => (
            <div key={cat}>
              <div style={gt.catLabel}>{CAT_EMOJI_MAP[cat]} {cat}</div>
              {catItems.map(item => (
                <div key={item.name} style={{ ...gt.item, opacity: item.checked ? 0.45 : 1 }}
                  onClick={() => onToggle(item.name)}>
                  <div style={{ ...gt.checkbox, ...(item.checked ? gt.checkboxDone : {}) }}>
                    {item.checked && <span style={gt.checkMark}>✓</span>}
                  </div>
                  <span style={{ ...gt.itemName, textDecoration: item.checked ? 'line-through' : 'none' }}>
                    {item.name}
                  </span>
                  <button style={gt.removeBtn}
                    onClick={e => { e.stopPropagation(); onRemove(item.name); }}>✕</button>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer actions */}
      {totalCount > 0 && (
        <div style={gt.footer}>
          {doneCount > 0 && (
            <button style={{ ...gt.footerBtn, color: 'var(--accent-green)' }}
              onClick={onClearDone}>Clear done ({doneCount})</button>
          )}
          <button style={{ ...gt.footerBtn, color: 'var(--text-muted)' }}
            onClick={onClearAll}>Clear all</button>
          <span style={gt.footerCount}>{totalCount - doneCount} left</span>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function KitchenPanel({
  pantry, onTogglePantry, onClearPantry,
  groceryItems, onToggleGrocery, onRemoveGrocery, onClearDoneGrocery, onClearAllGrocery, onAddGrocery,
  defaultTab = 'pantry',
}) {
  const [open,      setOpen     ] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const isMobile = useIsMobile();
  const ref = useRef(null);

  // Close on outside click (desktop)
  useEffect(() => {
    if (!open || isMobile) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, isMobile]);

  // Lock body scroll on mobile sheet
  useEffect(() => {
    document.body.style.overflow = isMobile && open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, open]);

  const groceryCount  = groceryItems.filter(i => !i.checked).length;
  const pantryCount   = pantry.size;

  const openTab = (tab) => { setActiveTab(tab); setOpen(true); };

  const content = (
    <div style={s.panel}>
      {/* Tab bar */}
      <div style={s.tabs}>
        {[
          { id: 'pantry',  label: '🥔 My Pantry',    badge: pantryCount,  badgeGrey: true },
          { id: 'grocery', label: '🛒 Grocery List',  badge: groceryCount, badgeOrange: true },
        ].map(tab => (
          <button key={tab.id}
            style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.badge > 0 && (
              <span style={{
                ...s.tabBadge,
                ...(tab.badgeOrange ? { background: 'var(--accent-orange)', color: '#fff' } : { background: 'var(--bg-pill)', color: 'var(--text-muted)' }),
              }}>{tab.badge}</span>
            )}
          </button>
        ))}
        <button style={s.closeBtn} onClick={() => setOpen(false)}>✕</button>
      </div>

      {/* Tab content */}
      {activeTab === 'pantry' ? (
        <PantryTab pantry={pantry} onToggle={onTogglePantry} onClear={onClearPantry} />
      ) : (
        <GroceryTab
          items={groceryItems}
          onToggle={onToggleGrocery}
          onRemove={onRemoveGrocery}
          onClearDone={onClearDoneGrocery}
          onClearAll={onClearAllGrocery}
          onAdd={onAddGrocery}
        />
      )}
    </div>
  );

  return (
    <div ref={ref} style={s.wrap}>
      {/* Two trigger buttons */}
      <div style={s.triggers}>
        <button style={{ ...s.trigger, ...(activeTab === 'pantry' && open ? s.triggerActive : {}) }}
          onClick={() => open && activeTab === 'pantry' ? setOpen(false) : openTab('pantry')}>
          🥔 My Pantry
          {pantryCount > 0 && <span style={{ ...s.badge, background: 'var(--border-strong)', color: 'var(--text-secondary)' }}>{pantryCount}</span>}
        </button>
        <button style={{ ...s.trigger, ...(activeTab === 'grocery' && open ? s.triggerActive : {}) }}
          onClick={() => open && activeTab === 'grocery' ? setOpen(false) : openTab('grocery')}>
          🛒 Grocery List
          {groceryCount > 0 && <span style={{ ...s.badge, background: 'var(--accent-orange)', color: '#fff' }}>{groceryCount}</span>}
        </button>
      </div>

      {/* Desktop dropdown */}
      {!isMobile && open && (
        <div style={s.dropdown}>{content}</div>
      )}

      {/* Mobile bottom sheet */}
      {isMobile && open && (
        <>
          <div style={s.backdrop} onClick={() => setOpen(false)} />
          <div style={s.sheet}>
            <div style={s.handle} />
            {content}
          </div>
        </>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  wrap:    { position: 'relative', display: 'inline-flex', flexDirection: 'column' },
  triggers:{ display: 'flex', gap: '6px', flexWrap: 'wrap' },
  trigger: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '6px 13px',
    border: '1px solid var(--border-base)',   // always reset — never inherits accent
    borderRadius: 'var(--radius-pill)', background: 'var(--bg-pill)',
    color: 'var(--text-secondary)', cursor: 'pointer',
    fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: '500',
    transition: 'all var(--transition)', whiteSpace: 'nowrap',
  },
  triggerActive: {
    border: '1px solid var(--accent-orange)',  // explicit — overrides base completely
    color: 'var(--accent-orange)',
    background: 'rgba(189,80,22,0.07)',
  },
  badge: {
    minWidth: '16px', height: '16px', borderRadius: '999px',
    fontSize: '9px', fontWeight: '700',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 4px',
  },

  // Panel shell
  panel: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  tabs:  {
    display: 'flex', alignItems: 'center',
    borderBottom: '1px solid var(--border-base)',
    background: 'var(--bg-card)', flexShrink: 0,
  },
  tab: {
    flex: 1, padding: '11px 8px', border: 'none', background: 'none',
    cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '12.5px',
    fontWeight: '500', color: 'var(--text-muted)',
    borderBottom: '2.5px solid transparent', transition: 'all var(--transition)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  },
  tabActive: {
    color: 'var(--accent-orange)',
    borderBottomColor: 'var(--accent-orange)',
    fontWeight: '700',
    background: 'var(--bg-surface)',
  },
  tabBadge: {
    minWidth: '16px', height: '16px', borderRadius: '999px',
    fontSize: '9px', fontWeight: '700',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 4px',
  },
  closeBtn: {
    width: '28px', height: '28px', border: 'none', background: 'none',
    color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px',
    marginRight: '6px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  // Desktop dropdown
  dropdown: {
    position: 'absolute', top: 'calc(100% + 10px)', left: 0,
    width: '420px', maxHeight: '540px',
    background: 'var(--bg-surface)', border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-lg)',
    zIndex: 300,
    display: 'flex', flexDirection: 'column',
    animation: 'fadeUp 0.16s ease',
    overflow: 'hidden',
  },

  // Mobile sheet
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 400, backdropFilter: 'blur(3px)' },
  sheet: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401,
    background: 'var(--bg-surface)', borderRadius: '20px 20px 0 0',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.2)', maxHeight: '88vh',
    display: 'flex', flexDirection: 'column',
    animation: 'sheetUp 0.28s cubic-bezier(0.32,0.72,0,1)', overflow: 'hidden',
  },
  handle: {
    width: '36px', height: '4px', borderRadius: '2px',
    background: 'var(--border-strong)', margin: '12px auto 0', flexShrink: 0,
  },
};

// Pantry tab styles
const pt = {
  wrap:        { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' },
  searchRow:   { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderBottom: '1px solid var(--border-base)', flexShrink: 0 },
  searchInput: {
    flex: 1, padding: '7px 10px', fontFamily: 'var(--font-body)', fontSize: '13px',
    border: '1px solid var(--border-base)', borderRadius: 'var(--radius-md)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  },
  clearBtn: {
    padding: '5px 10px', border: '1px solid var(--border-base)', borderRadius: 'var(--radius-pill)',
    background: 'none', color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
    fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  catRow: {
    display: 'flex', gap: '4px', padding: '8px 12px',
    borderBottom: '1px solid var(--border-base)', flexShrink: 0,
    overflowX: 'auto', scrollbarWidth: 'none',
  },
  catBtn: {
    padding: '4px 10px', border: '1px solid var(--border-base)', borderColor: 'var(--border-base)',
    borderRadius: 'var(--radius-pill)',
    background: 'none', color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
    fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
    outline: 'none',
  },
  catBtnActive: { background: 'var(--accent-orange)', borderColor: 'var(--accent-orange)', color: '#fff' },
  hint: { fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)', padding: '6px 12px 0', margin: 0, flexShrink: 0 },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
    gap: '6px', padding: '8px 12px', overflowY: 'auto', flex: 1, minHeight: 0,
  },
  tile: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '8px 4px', border: '1.5px solid var(--border-base)', borderColor: 'var(--border-base)',
    borderRadius: 'var(--radius-md)', background: 'var(--bg-card)',
    cursor: 'pointer', position: 'relative', gap: '3px',
    transition: 'all var(--transition)', fontFamily: 'var(--font-body)',
    outline: 'none',
  },
  tileOwned: {
    borderColor: 'var(--accent-green)', background: 'var(--accent-green-bg)',
  },
  tileEmoji: { fontSize: '22px' },
  tileLabel: { fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center', lineHeight: '1.2' },
  tileCheck: {
    position: 'absolute', top: '3px', right: '4px',
    fontSize: '9px', fontWeight: '800', color: 'var(--accent-green)',
  },
  footer: { padding: '8px 12px', borderTop: '1px solid var(--border-base)', flexShrink: 0 },
  footerText: { fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' },
};

// Grocery tab styles
const gt = {
  wrap: { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' },
  addRow: {
    display: 'flex', gap: '8px', padding: '10px 12px',
    borderBottom: '1px solid var(--border-base)', flexShrink: 0,
  },
  addInput: {
    flex: 1, padding: '8px 10px', fontFamily: 'var(--font-body)', fontSize: '13px',
    border: '1px solid var(--border-base)', borderRadius: 'var(--radius-md)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
  },
  addBtn: {
    padding: '8px 14px', border: 'none', borderRadius: 'var(--radius-md)',
    background: 'var(--accent-orange)', color: '#fff',
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    flexShrink: 0,
  },
  progressTrack: { height: '2px', background: 'var(--border-base)', flexShrink: 0 },
  progressFill:  { height: '100%', background: 'var(--accent-green)', transition: 'width 0.4s ease' },
  list: { flex: 1, overflowY: 'auto', padding: '4px 0' },
  catLabel: {
    padding: '8px 14px 3px', fontSize: '10px', fontWeight: '700',
    letterSpacing: '0.8px', textTransform: 'uppercase',
    color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
  },
  item: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 14px', cursor: 'pointer',
    borderBottom: '1px solid var(--border-base)',
    transition: 'background var(--transition)',
  },
  checkbox: {
    width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
    border: '1.5px solid var(--border-strong)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all var(--transition)',
  },
  checkboxDone: { background: 'var(--accent-green)', borderColor: 'var(--accent-green)' },
  checkMark: { fontSize: '10px', color: '#fff', fontWeight: '700' },
  itemName: { flex: 1, fontFamily: 'var(--font-body)', fontSize: '13.5px', lineHeight: '1.3', color: 'var(--text-primary)' },
  removeBtn: {
    width: '20px', height: '20px', border: 'none', background: 'none',
    color: 'var(--text-muted)', fontSize: '11px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5,
  },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 20px', gap: '6px', textAlign: 'center' },
  emptyIcon: { fontSize: '36px' },
  emptyText: { fontFamily: 'var(--font-display)', fontSize: '16px', color: 'var(--text-primary)', margin: 0 },
  emptySub:  { fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-muted)', margin: 0, lineHeight: '1.6' },
  footer: {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 14px',
    borderTop: '1px solid var(--border-base)', flexShrink: 0,
  },
  footerBtn: {
    padding: '7px 12px', border: '1px solid var(--border-base)', borderRadius: 'var(--radius-pill)',
    background: 'none', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  footerCount: { marginLeft: 'auto', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' },
};
